import { AccountCircleOutlined, DarkMode, LightMode, SupervisedUserCircleOutlined } from "@mui/icons-material";
import { Divider, MenuItem } from "@mui/material";
import { getSansSDK, useUserInfo } from "@sans-service-tier/sdk-react";
import { getLoginAdapter } from "@sans-service-tier/sdk-web";
import { MouseEventHandler, useState } from "react";
import { useNavigate } from "react-router";

import { ModeType } from "../../apollo/typePolicies/LocalStorage";
import { usePersistedSettings } from "../../hooks/usePersistedSettings";
import {
  accountDashboardUrl,
  impersonationAdminReturnUrlKey,
  impersonationLoadingMessageKey,
  impersonationReturnToAdminKey,
} from "../../util/constants";
import { MenuButton, MenuLink, StyledMenu } from "./UserMenu.elements";

export async function unimpersonate(): Promise<void> {
  sessionStorage.setItem(impersonationLoadingMessageKey, "Switching back to admin");
  sessionStorage.setItem(impersonationReturnToAdminKey, "true");
  await getSansSDK().api.tokens.refreshTokens();
  const returnUrl = sessionStorage.getItem(impersonationAdminReturnUrlKey);
  if (returnUrl) {
    sessionStorage.removeItem(impersonationAdminReturnUrlKey);
    window.location.assign(returnUrl);
  } else {
    window.location.reload();
  }
}

export function UserMenu({ library }: { library: boolean }): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { mode, setMode } = usePersistedSettings();
  const userInfo = useUserInfo() as unknown as Record<string, string> | undefined;
  const navigate = useNavigate();
  const loginAdapter = getLoginAdapter();

  const open = Boolean(anchorEl);
  const openMenu: MouseEventHandler<HTMLButtonElement> = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const links = [
    {
      label: "Account dashboard",
      href: accountDashboardUrl,
      id: "menu-account-dashboard",
    },
  ];

  const modeIcons = {
    dark: <DarkMode fontSize="small" />,
    light: <LightMode fontSize="small" />,
  };

  const toggleMode = () => {
    const updatedMode = mode === "light" ? "dark" : "light";
    setMode(updatedMode);
  };

  return (
    <>
      <MenuButton
        tabIndex={1}
        size="medium"
        edge="start"
        color="inherit"
        aria-label="Account profile menu"
        id="menu-account-profile"
        onClick={openMenu}
      >
        {userInfo?.assumedBy ? (
          <SupervisedUserCircleOutlined fontSize="large" />
        ) : (
          <AccountCircleOutlined fontSize="large" />
        )}
      </MenuButton>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!library && (
          <MenuItem id="menu-my-courses" onClick={() => navigate("/")} sx={{ display: "flex", gap: 1 }}>
            My courses
          </MenuItem>
        )}
        {links.map((link, idx) => (
          <MenuItem onClick={handleClose} key={idx}>
            <MenuLink id={link.id} href={link.href}>
              {link.label}
            </MenuLink>
          </MenuItem>
        ))}
        <MenuItem id="menu-dark-mode" onClick={toggleMode} sx={{ display: "flex", gap: 1 }}>
          {mode && modeIcons[mode as ModeType]} Dark Mode: {mode === "dark" ? "On" : "Off"}
        </MenuItem>
        <Divider />
        {userInfo?.assumedBy ? (
          <MenuItem id="menu-unimpersonate" onClick={unimpersonate}>
            Unimpersonate
          </MenuItem>
        ) : (
          <MenuItem id="menu-logout" onClick={() => loginAdapter.logout()}>
            Logout
          </MenuItem>
        )}
      </StyledMenu>
    </>
  );
}
